import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { SweetAlertService } from "src/app/services/sweet-alert-service/sweet-alert.service";
import { AuthService } from "src/app/pages/auth/services/auth.service";
import { environment } from "src/environments/environment";
import { SKIP_UNAUTHORIZED_HANDLER } from "./http-context-tokens";

let globalErrorAlertInProgress = false;
const SKIP_GLOBAL_ERROR_DIALOG_HEADER = "X-Skip-Global-Error-Dialog";

const isLoginRequest = (req: HttpRequest<unknown>): boolean =>
  req.method.toUpperCase() === "POST" && req.url.replace(/^\/+/, "") === "session";

const isSessionRestoreRequest = (req: HttpRequest<unknown>): boolean =>
  req.method.toUpperCase() === "GET" && req.url.replace(/^\/+/, "") === "session/verify";

const isHtmlErrorMessage = (value: string): boolean => {
  const trimmedValue = value.trim().toLowerCase();

  return (
    trimmedValue.startsWith("<!doctype html") ||
    trimmedValue.startsWith("<html") ||
    /<\/?(html|head|body|title|style|script|pre)(\s|>|\/)/i.test(value)
  );
};

const normalizeErrorMessage = (value: string): string | undefined => {
  const message = value.trim();

  if (message === "" || isHtmlErrorMessage(message)) {
    return undefined;
  }

  return message;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "string") {
    return normalizeErrorMessage(error);
  }

  if (error == null || typeof error !== "object") {
    return undefined;
  }

  const errorObject = error as Record<string, unknown>;
  const value = errorObject["message"] ?? errorObject["errors"] ?? errorObject["error"];

  if (typeof value === "string") {
    return normalizeErrorMessage(value);
  }

  if (Array.isArray(value) && typeof value[0] === "string") {
    return normalizeErrorMessage(value[0]);
  }

  return undefined;
};

const showGlobalErrorDialog = (
  sweetAlertService: SweetAlertService,
  title: string,
  message: string,
): void => {
  if (globalErrorAlertInProgress) {
    return;
  }

  globalErrorAlertInProgress = true;

  void sweetAlertService.swalAlert(
    title,
    message,
    true,
  ).finally(() => {
    globalErrorAlertInProgress = false;
  });
};

const showServerErrorDialog = (
  sweetAlertService: SweetAlertService,
  message?: string,
): void => {
  showGlobalErrorDialog(
    sweetAlertService,
    "เกิดข้อผิดพลาด",
    message != null && message !== "" ? message : "ระบบขัดข้อง กรุณาลองใหม่อีกครั้ง",
  );
};

const showNotFoundErrorDialog = (sweetAlertService: SweetAlertService): void => {
  showGlobalErrorDialog(
    sweetAlertService,
    "ไม่พบข้อมูล",
    "ไม่พบข้อมูลที่ต้องการ กรุณาลองใหม่อีกครั้ง",
  );
};

export const httpClientInterceptor: HttpInterceptorFn = (req, next) => {
  const apiUrl = environment.apiUrl;
  const authService = inject(AuthService);
  const sweetAlertService = inject(SweetAlertService);
  const skipGlobalErrorDialog = req.headers.get(SKIP_GLOBAL_ERROR_DIALOG_HEADER) === "true";

  // ❌ skip assets
  if (req.url.includes("assets/")) {
    return next(req);
  }

  // ✅ prepend API URL
  const reqUrl = apiUrl + req.url;

  // ✅ clone with base headers
  const modifiedReq = req.clone({
    url: reqUrl,
    withCredentials: true,
    headers: req.headers.delete(SKIP_GLOBAL_ERROR_DIALOG_HEADER),
    setHeaders: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
    },
  });
  // ✅ handle response & errors
  return next(modifiedReq).pipe(
    map((event) => event),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 401) {
          const skipUnauthorizedHandler = req.context.get(SKIP_UNAUTHORIZED_HANDLER);

          if (!isLoginRequest(req) && !isSessionRestoreRequest(req) && !skipUnauthorizedHandler) {
            void authService.handleUnauthorized();
          }
        }

        if (!skipGlobalErrorDialog && err.status === 500) {
          showServerErrorDialog(sweetAlertService, getErrorMessage(err.error));
        }

        if (!skipGlobalErrorDialog && err.status === 404) {
          showNotFoundErrorDialog(sweetAlertService);
        }

        return throwError(() => err);
      }
      return throwError(() => err);
    }),
  );
};
